/**
 * SimpleSystem - Système simple pour les aventures standard
 * Identique à Flutter BaseSystem (qui gère les aventures simples)
 */

import React from 'react';
import GameRegistry from '../GameRegistry';
import {ListAdventure} from '../../models/ListAdventure';
import {PlayerStep} from '../../models/PlayerStep';
import {GameState, GameStateStatus} from '../../models/GameState';
import AdventureCard from '../../components/AdventureCard';
import BaseStepWidget from '../../components/BaseStepWidget';
import {AttributeConditionType} from './conditions/AttributeConditionType';
import {NoAttributeConditionType} from './conditions/NoAttributeConditionType';
import {ExtraFieldConditionType} from './conditions/ExtraFieldConditionType';
import {StepVisitedConditionType} from './conditions/StepVisitedConditionType';
import {HasBoughtConditionType} from './conditions/HasBoughtConditionType';
import {TrueConditionType} from './conditions/TrueConditionType';
import {StepSucceedConditionType} from './conditions/StepSucceedConditionType';
import {StepFailedConditionType} from './conditions/StepFailedConditionType';
import {StepNotSucceedNorFailedConditionType} from './conditions/StepNotSucceedNorFailedConditionType';
import {HasNotBoughtConditionType} from './conditions/HasNotBoughtConditionType';
import {DoesNotComeFromConditionType} from './conditions/DoesNotComeFromConditionType';
import {PickUpActionType} from './actions/PickUpActionType';
import {DropActionType} from './actions/DropActionType';
import {RestartActionType} from './actions/RestartActionType';
import {UseBackupActionType} from './actions/UseBackupActionType';
import {ExtraFieldType} from './types/ExtraFieldType';

export class SimpleSystem {
  private _conditionsRegistered = false;
  private _stepBuildersRegistered = false;
  private _actionsRegistered = false;
  protected _systemType?: string; // Type du système (défini lors de registerSystem)

  constructor() {
    // Ne pas enregistrer les conditions dans le constructeur pour éviter les dépendances circulaires
    // L'enregistrement sera fait explicitement via registerConditionTypes()
    this._registerActions();
  }

  /**
   * Enregistre les types d'actions
   */
  private _registerActions(): void {
    if (this._actionsRegistered) {
      return;
    }

    GameRegistry.registerActionType(new PickUpActionType());
    GameRegistry.registerActionType(new DropActionType());
    GameRegistry.registerActionType(new RestartActionType());
    GameRegistry.registerActionType(new UseBackupActionType());

    this._actionsRegistered = true;
    console.log('[SimpleSystem] Registered action types: pick_up, drop, restart, use_backup');
  }

  /**
   * Enregistre les types de conditions
   * À appeler explicitement après l'initialisation de GameRegistry
   */
  registerConditionTypes(): void {
    if (this._conditionsRegistered) {
      return; // Déjà enregistrées
    }

    // Noms réels utilisés par le backend (GameConstants.java)
    GameRegistry.registerConditionType('COND_ATTRIBUTE', () => new AttributeConditionType());
    GameRegistry.registerConditionType('COND_NO_ATTRIBUTE', () => new NoAttributeConditionType());
    GameRegistry.registerConditionType('COND_EXTRA_FIELD', () => new ExtraFieldConditionType());
    GameRegistry.registerConditionType('COND_STEP_VISITED', () => new StepVisitedConditionType());
    GameRegistry.registerConditionType('COND_COMES_FROM', () => new StepVisitedConditionType()); // Alias
    GameRegistry.registerConditionType('COND_HAS_BOUGHT', () => new HasBoughtConditionType());
    GameRegistry.registerConditionType('has_bought', () => new HasBoughtConditionType()); // Alias
    
    // Nouvelles conditions Core
    GameRegistry.registerConditionType('COND_STEP_SUCCEED', () => new StepSucceedConditionType());
    GameRegistry.registerConditionType('COND_SUCCEED', () => new StepSucceedConditionType()); // Alias
    GameRegistry.registerConditionType('COND_STEP_FAILED', () => new StepFailedConditionType());
    GameRegistry.registerConditionType('COND_FAILED', () => new StepFailedConditionType()); // Alias
    GameRegistry.registerConditionType('COND_STEP_NOT_SUCCEED_NOR_FAILED', () => new StepNotSucceedNorFailedConditionType());
    GameRegistry.registerConditionType('COND_NOT_SUCCEED_NOR_FAILED', () => new StepNotSucceedNorFailedConditionType()); // Alias
    GameRegistry.registerConditionType('COND_HAS_NOT_BOUGHT', () => new HasNotBoughtConditionType());
    GameRegistry.registerConditionType('COND_DOES_NOT_COMES_FROM', () => new DoesNotComeFromConditionType());

    this._conditionsRegistered = true;
    console.log('[SimpleSystem] Registered condition types (including new Core conditions)');
  }

  /**
   * Enregistre les builders de step
   * À appeler explicitement après l'initialisation de GameRegistry
   */
  registerStepBuilders(): void {
    if (this._stepBuildersRegistered) {
      return; // Déjà enregistrés
    }

    // STEP_SIMPLE : builder de base
    GameRegistry.registerStepBuilder('STEP_SIMPLE', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });

    // STEP_FAILED : utiliser BaseStepWidget (les boutons de replay seront gérés par BaseStepWidget si nécessaire)
    GameRegistry.registerStepBuilder('STEP_FAILED', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });

    // STEP_SUCCEED : utiliser BaseStepWidget
    GameRegistry.registerStepBuilder('STEP_SUCCEED', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });

    // Alias pour les autres formats
    GameRegistry.registerStepBuilder('SIMPLE', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });
    GameRegistry.registerStepBuilder('FAILED', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });
    GameRegistry.registerStepBuilder('SUCCEED', (step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });

    // Définir le builder par défaut
    GameRegistry.setDefaultStepBuilder((step: PlayerStep) => {
      return React.createElement(BaseStepWidget, {step});
    });

    this._stepBuildersRegistered = true;
    console.log('[SimpleSystem] Registered step builders');
  }

  /**
   * 1) Carte dans la liste des aventures
   */
  buildListAdventureCard({
    adventure,
    play,
    downloadOrUpdate,
    isDownloading = false,
    downloadProgress = 0.0,
  }: {
    adventure: ListAdventure;
    play: () => void;
    downloadOrUpdate: (adventure: ListAdventure) => Promise<void>;
    isDownloading?: boolean;
    downloadProgress?: number;
  }): React.ReactElement {
    return React.createElement(AdventureCard, {
      adventure,
      onPlay: play,
      onDownloadOrUpdate: downloadOrUpdate,
      isDownloading,
      downloadProgress,
    });
  }

  /**
   * 2) Menu en-jeu (3 slots + crédits + hall of fame)
   * TODO: Utiliser le vrai BaseMenuWidget avec tous les slots et l'image d'aventure
   */
  buildGameMenu({
    onPlayStep,
    onCredits,
    onHallOfFame,
  }: {
    onPlayStep: () => void;
    onCredits: () => void;
    onHallOfFame: () => void;
  }): React.ReactElement {
    // Pour l'instant, retourner un placeholder
    // TODO: Implémenter BaseMenuWidget avec l'image d'aventure, les 3 slots, crédits, hall of fame
    return React.createElement(
      'div',
      {style: {padding: 20, color: '#fff', backgroundColor: '#1a1a1a'}},
      React.createElement('div', null, 'Menu - TODO: Implémenter BaseMenuWidget'),
      React.createElement('button', {onClick: onPlayStep}, 'Jouer'),
      React.createElement('button', {onClick: onCredits}, 'Crédits'),
      React.createElement('button', {onClick: onHallOfFame}, 'Hall of Fame'),
    );
  }

  /**
   * 3) Crédits en-jeu
   * TODO: Implémenter BaseCreditsWidget
   */
  buildGameCredits({onBack}: {onBack: () => void}): React.ReactElement {
    // Pour l'instant, retourner un placeholder
    return React.createElement(
      'div',
      {style: {padding: 20}},
      'Crédits - À implémenter',
    );
  }

  /**
   * 4) Hall of Fame en-jeu
   * TODO: Implémenter BaseHallOfFameWidget
   */
  buildGameHallOfFame({onBack}: {onBack: () => void}): React.ReactElement {
    // Pour l'instant, retourner un placeholder
    return React.createElement(
      'div',
      {style: {padding: 20}},
      'Hall of Fame - À implémenter',
    );
  }

  /**
   * Retourne les types d'extraFields déclarés pour l'aventure dans ce système
   * Agnostique : le GameStateHelper utilise cette méthode pour savoir quels extraFields initialiser
   * Par défaut, SimpleSystem n'a pas d'extraFields d'aventure
   * Les systèmes enfants doivent override cette méthode pour retourner leurs types spécifiques
   */
  getAdventureExtraFieldTypes(): ExtraFieldType[] {
    // SimpleSystem n'a pas d'extraFields d'aventure par défaut
    // Les systèmes enfants (D6System, EscapeSystem) override cette méthode
    return [];
  }

  /**
   * Met à jour le gameState selon la logique du système de jeu
   */
  updateGameState(gameState: GameState, currentStep: PlayerStep): GameState {
    // Logique de base : vérifier le type de step
    let newState: GameStateStatus | undefined;
    const stepType = currentStep.type.toUpperCase();
    if (stepType === 'STEP_SUCCEED') {
      newState = GameStateStatus.SUCCEED;
    } else if (stepType === 'STEP_FAILED') {
      newState = GameStateStatus.FAILED;
    }

    if (newState != null) {
      return {
        ...gameState,
        state: newState,
      };
    }
    return gameState;
  }
}
